import React, { useState } from 'react';
import { Typography, Paper, Box, Button, IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { resolveBoothIcon } from '../../utils/IconMap';
import { useLocalization } from "../../context/LocalizationContext";
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';
import SubmitForApprovalButton from '../approval/SubmitForApprovalButton';
import WithdrawButton from '../approval/WithdrawButton';
import ContentPreviewDrawer from '../approval/ContentPreviewDrawer';
import { usePermissions } from '../../hooks/usePermissions';
import { CONTENT_TYPES } from '../../constants/contentTypes';

const viewBtnSx = {
  borderColor: '#3b82f6',
  color: '#1d4ed8',
  bgcolor: '#eff6ff',
  borderRadius: '8px',
  px: 1.5,
  py: 0.5,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
};

const BoothCard = ({ item, onSuccess, onViewChanges, onEditRejected, onEdit, preview = false }) => {
  const navigate = useNavigate();
  const { locale, t } = useLocalization();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/booth', 'booth-edit');
  const [viewProposalOpen, setViewProposalOpen] = useState(false);

  const boothName =
    item.localizedPairs?.values?.find((v) => v.localeId === locale)?.value ||
    item.localizedPairs?.values?.[0]?.value ||
    t(item.localizedPairs?.key, '') ||
    '';
  const isSnapshot    = item._viewType === 'snapshot';
  const isLocked      = item.approvalStatus === 'pending' || item.approvalStatus === 'rejected';
  const canDirectEdit = item.contentStatus === 'draft' && !isLocked;

  // Submit only when live draft with no ca record exists
  const hasApprovalAction = !preview && !isSnapshot && canDirectEdit && item.contentStatus === 'draft' && canEdit;

  const viewApprovalRecord = item.approvalId ? {
    approvalId:       item.approvalId,
    actionType:       item.approvalActionType,
    contentType:      CONTENT_TYPES.BOOTH,
    sectionLabel:     'Booth',
    contentName:      boothName,
    submittedByName: '',
    submittedAt:      null,
    status:            item.approvalStatus,
  } : null;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 4,
        bgcolor: "white",
        border: "2px solid transparent",
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        cursor: preview || isSnapshot ? "default" : "pointer",
        transition: "all 0.2s ease",
        ...(!preview && {
          "&:hover": {
            borderColor: "#5383b3",
            boxShadow: "0 8px 24px rgba(10,50,109,0.15)",
            transform: "translateY(-2px)",
            "& .booth-underline": { width: "36px" },
          },
        }),
      }}
      onClick={preview || isSnapshot ? undefined : () => navigate(`/booth/${item.id}`)}
    >
      {/* Snapshot version banner */}
      {isSnapshot && (
        <Box sx={{
          bgcolor: item.approvalStatus === 'pending' ? '#d97706' : '#dc2626',
          color: 'white', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 600, textAlign: 'center',
        }}>
          {item.approvalStatus === 'pending' ? 'Proposed — Pending Review' : 'Proposed — Rejected'}
        </Box>
      )}

      {/* Main icon + name area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          p: 2,
          minHeight: { xs: 120, sm: 140, md: 160 },
        }}
      >
        <Box
          sx={{
            bgcolor: "#0a326d",
            borderRadius: "12px",
            width: { xs: 44, sm: 52, md: 60 },
            height: { xs: 44, sm: 52, md: 60 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(10,50,109,0.25)",
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={resolveBoothIcon(item)}
            alt={boothName}
            sx={{ width: { xs: 26, sm: 30, md: 36 }, height: { xs: 26, sm: 30, md: 36 } }}
          />
        </Box>
        <Typography
          variant="subtitle2"
          sx={{
            textAlign: "center",
            lineHeight: 1.3,
            px: 1.5,
            fontSize: 12,
            fontWeight: 800,
            wordBreak: "break-word",
            color: "#1e3a5f",
          }}
        >
          {boothName}
        </Typography>

        {/* Approval status badge — centered below booth name */}
        {(isSnapshot ? item.approvalStatus : item.contentStatus) && (
          <Box onClick={(e) => e.stopPropagation()}>
            <ApprovalStatusBadge
              status={isSnapshot ? item.approvalStatus : item.contentStatus}
              actionType={item.approvalActionType}
            />
          </Box>
        )}

        <Box
          className="booth-underline"
          sx={{
            height: "2px",
            width: 0,
            bgcolor: "#5383b3",
            borderRadius: "2px",
            transition: "width 0.25s ease",
          }}
        />
      </Box>

      {/* Live draft card: Submit for approval */}
      {hasApprovalAction && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ borderTop: "1px solid #f1f5f9", px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, flexWrap: "wrap" }}
        >
          <SubmitForApprovalButton
            contentType={CONTENT_TYPES.BOOTH}
            contentId={item.id}
            contentName={boothName}
            onSubmitted={() => onSuccess?.('pending')}
          />
        </Box>
      )}

      {/* Live locked card: View button (navigates to booth page) */}
      {!preview && !isSnapshot && isLocked && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ borderTop: "1px solid #f1f5f9", px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}
        >
          <Tooltip title="View booth (locked — proposal pending)">
            <IconButton
              size="small"
              onClick={() => navigate(`/booth/${item.id}`)}
              sx={{ color: "#3b82f6", "&:hover": { bgcolor: "#eff6ff" } }}
            >
              <VisibilityIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Live unlocked card: Edit (direct or propose) */}
      {!preview && !isSnapshot && !isLocked && canEdit && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ borderTop: "1px solid #f1f5f9", px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}
        >
          <Tooltip title={canDirectEdit ? 'Edit booth' : 'Propose edit (requires approval)'}>
            <IconButton
              size="small"
              onClick={() => onEdit?.(item, canDirectEdit)}
              sx={{ color: "#64748b", "&:hover": { bgcolor: "#eff6ff", color: "#1d4ed8" } }}
            >
              <EditOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Snapshot card: View Proposal + Withdraw (pending) or Resubmit + Dismiss (rejected) */}
      {!preview && isSnapshot && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ borderTop: "1px solid #f1f5f9", px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, flexWrap: "wrap" }}
        >
          {viewApprovalRecord && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
              onClick={() => setViewProposalOpen(true)}
              sx={viewBtnSx}
            >
              View
            </Button>
          )}
          {item.approvalStatus === 'pending' && item.approvalId && (
            <WithdrawButton approvalId={item.approvalId} onWithdrawn={() => onSuccess?.(item.approvalActionType === 'create' ? 'draft' : 'live')} />
          )}
          {item.approvalStatus === 'rejected' && item.approvalId && (
            <WithdrawButton approvalId={item.approvalId} onWithdrawn={() => onSuccess?.(item.approvalActionType === 'create' ? 'draft' : 'live')} variant="dismiss" />
          )}
        </Box>
      )}

      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => setViewProposalOpen(false)}
          readOnly
          onEdit={onEditRejected ? (snap) => { setViewProposalOpen(false); onEditRejected(snap, item._liveId ?? item.id, item.approvalActionType, boothName); } : undefined}
        />
      )}
    </Paper>
  );
};

export default BoothCard;
